import { Box, Spinner, chakra } from '@chakra-ui/react';
import { first } from 'lodash';
import * as math from 'mathjs';
import {
  Dispatch,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { isHotkeyPressed, useHotkeys } from 'react-hotkeys-hook';
import invariant from 'tiny-invariant';
import { useFlag } from '@unleash/proxy-client-react';
import {
  calculatePlaneEquation,
  projectPointOnPlane,
  to3dMultiPolygon,
  to3dPoints,
  toPlanarPoint,
  toPlanarPoints,
} from '../../../domain/geometry/algorithms/util/plane';
import {
  LineSegment,
  LineSegment2,
  LinearRing,
  MultiPolygon,
  Plane,
  Point,
  Point2,
} from '../../../domain/geometry/geometric-types';
import {
  addEdge,
  copy,
  getInvalidEdges,
  intersects,
} from '../../../services/viewer/shape-drawing/intersections';
import { getShape } from '../../../services/viewer/shape-drawing/one-click-shapes';
import { getCanvasMousePosition, useViewer } from '../../common/ForgeViewer';
import { DraggableEdge, Edge, getPositionOnEdge } from './Edge';
import { DragFeedbackLine } from './DragFeedbackLine';
import { EditableLengthLabel } from './EditableLengthLabel';
import { Polygon } from './Polygon';
import {
  InitialShapeDrawingState,
  PolygonShapeDrawingResult,
  ShapeDrawingMode,
} from './ShapeDrawing';
import { DraggableVertex, Vertex } from './Vertex';
import {
  PolygonDrawingAction,
  PolygonDrawingActionType,
  PolygonDrawingState,
  polygonDrawingReducer,
} from './polygon-drawing-reducer';
import { getRingsFromIntersections } from './shape-intersection';
import {
  LinearRing2WithId,
  LinearRing3WithId,
  Point3WithId,
  createIntermediateEdge,
  createMultipolygon,
  generateIdForRing,
  generateIdsForEdges,
  generateIdsForPoints,
  getArcPoints,
  getPolygon2PathData,
  getPositions,
  getSnapPoint,
  intermediateEdgeId,
  isPrimaryMouseButton,
  projectInDOM,
  projectMouseOnPlane,
  toPoints2WithId,
  transformPolygonCoordinates,
  useCameraChangedEvent,
  useSnapper,
  useViewerClickEvent,
  calculatePrincipalAxes,
} from './util';
import { ExtrudedPolygon } from './ExtrudedPolygon';

type SelectedVertex =
  | { type: 'incomplete'; pointId: string }
  | { type: 'completed'; ringId: string; pointId: string };

type BoxSelection = {
  start: Point2;
  end: Point2;
};

function initializeState(
  initialState?: InitialShapeDrawingState[ShapeDrawingMode.Polygon]
): PolygonDrawingState {
  if (!initialState) {
    return {
      completedRings: [],
      completedRings2: [],
      incompleteRing: [],
      incompleteRing2: [],
      mousePointerPoint: null,
      plane: null,
      intersectionsForIncompleteRing: new Map(),
      intersectionsForCompletedRings: new Map(),
    };
  }
  const completedRings = initialState.multipolygon.polygons.flatMap(
    (polygon) => [
      generateIdForRing(generateIdsForPoints(polygon.exterior)),
      ...polygon.interiors.map((interior) =>
        generateIdForRing(generateIdsForPoints(interior))
      ),
    ]
  );
  const completedRings2: LinearRing2WithId[] = completedRings.map((ring) => ({
    id: ring.id,
    linearRing: toPoints2WithId(ring.linearRing, initialState.plane),
  }));
  return {
    completedRings,
    completedRings2,
    plane: initialState.plane,
    incompleteRing: [],
    incompleteRing2: [],
    mousePointerPoint: null,
    intersectionsForIncompleteRing: new Map(),
    intersectionsForCompletedRings: new Map(),
    thickness: initialState.thickness,
  };
}

export const PolygonShapeDrawing = ({
  initialState,
  viewer,
  onResult,
  isOneClickShapeMode,
  isRectangleMode,
  thickness,
  hiddenModelUrns,
  selectedCategories,
}: {
  initialState?: InitialShapeDrawingState[ShapeDrawingMode.Polygon];
  viewer: Autodesk.Viewing.GuiViewer3D;
  onResult: (result: PolygonShapeDrawingResult) => void;
  // For now we just reuse the same tool, consider splitting it into two
  isOneClickShapeMode: boolean;
  isRectangleMode?: boolean;
  selectedCategories?: string[];
  thickness?: number;
  hiddenModelUrns: string[];
}) => {
  const [state, dispatch] = useReducer(
    polygonDrawingReducer,
    initialState,
    initializeState
  );

  const [, setHistory] = useState<{
    past: PolygonDrawingState[];
    future: PolygonDrawingState[];
  }>({
    past: [],
    future: [],
  });

  const [isDragging, setIsDragging] = useState(false);
  const [isCalculatingIntersections, setIsCalculatingIntersections] =
    useState(false);
  const [calculationClickPoint, setCalculationClickPoint] =
    useState<Point | null>(null);
  const [selectedVertices, setSelectedVertices] = useState<SelectedVertex[]>(
    []
  );
  const [boxSelection, setBoxSelection] = useState<BoxSelection | null>(null);
  const boxSelectionRef = useRef<BoxSelection | null>(null);
  // Counter that increments on undo/redo to signal child components to clear drag feedback
  const [historyVersion, setHistoryVersion] = useState(0);

  const snapper = useSnapper(viewer);

  const pushHistorySnapshot = useCallback(() => {
    setHistory((current) => ({
      past: [...current.past, structuredClone(state) as PolygonDrawingState],
      future: [],
    }));
  }, [state]);

  const handleUndo = useCallback(() => {
    setHistory((current) => {
      if (current.past.length === 0) {
        return current;
      }
      const previous = current.past[current.past.length - 1];
      const newPast = current.past.slice(0, -1);

      // Push current state to future before restoring previous
      const currentSnapshot = structuredClone(state) as PolygonDrawingState;
      dispatch({
        type: PolygonDrawingActionType.RESET_STATE,
        state: previous,
      });

      // Increment history version to signal child components to clear drag feedback
      setHistoryVersion((v) => v + 1);

      return {
        past: newPast,
        future: [currentSnapshot, ...current.future],
      };
    });
  }, [dispatch, state]);

  const handleRedo = useCallback(() => {
    setHistory((current) => {
      if (current.future.length === 0) {
        return current;
      }
      const [next, ...restFuture] = current.future;
      const currentSnapshot = structuredClone(state) as PolygonDrawingState;

      dispatch({
        type: PolygonDrawingActionType.RESET_STATE,
        state: next,
      });

      // Increment history version to signal child components to clear drag feedback
      setHistoryVersion((v) => v + 1);

      return {
        past: [...current.past, currentSnapshot],
        future: restFuture,
      };
    });
  }, [dispatch, state]);

  useHotkeys(
    ['ctrl+z', 'meta+z'],
    () => {
      handleUndo();
    },
    { preventDefault: true },
    [handleUndo]
  );

  useHotkeys(
    ['ctrl+y', 'meta+y'],
    () => {
      handleRedo();
    },
    { preventDefault: true },
    [handleRedo]
  );

  const emitResult = useCallback(() => {
    const hasNoIntersections = [
      ...state.intersectionsForCompletedRings.values(),
    ].every((intersections) => intersections.length === 0);
    const hasNoNewPoints = state.incompleteRing.length === 0;
    const hasConfirmedRing = state.completedRings2.length > 0;

    const isValidResult =
      hasNoIntersections &&
      hasNoNewPoints &&
      hasConfirmedRing &&
      (thickness !== undefined ? math.abs(thickness) > 0.0001 : true);

    if (state.plane && isValidResult && !isDragging) {
      const ringPositions = state.completedRings2.map((ring) =>
        getPositions(ring.linearRing)
      );
      onResult({
        valid: true,
        multipolygon: to3dMultiPolygon(
          createMultipolygon(ringPositions),
          state.plane
        ),
        plane: state.plane,
        thickness,
      });
    } else {
      onResult({
        valid: false,
      });
    }
  }, [
    isDragging,
    onResult,
    state.completedRings2,
    state.incompleteRing.length,
    state.intersectionsForCompletedRings,
    state.plane,
    thickness,
  ]);

  useEffect(() => {
    if (!isDragging) {
      emitResult();
    }
  }, [emitResult, isDragging]);

  const handleVertexSelectionClick = useCallback(
    (vertex: SelectedVertex, isShiftPressed: boolean) => {
      setSelectedVertices((prev) => {
        const exists = prev.some((v) => {
          if (v.type !== vertex.type) {
            return false;
          }
          if (v.type === 'incomplete' && vertex.type === 'incomplete') {
            return v.pointId === vertex.pointId;
          }
          if (v.type === 'completed' && vertex.type === 'completed') {
            return v.ringId === vertex.ringId && v.pointId === vertex.pointId;
          }
          return false;
        });

        if (isShiftPressed) {
          // Multi-select: add if not selected, remove if already selected
          if (exists) {
            return prev.filter((v) => {
              if (v.type !== vertex.type) {
                return true;
              }
              if (v.type === 'incomplete' && vertex.type === 'incomplete') {
                return v.pointId !== vertex.pointId;
              }
              if (v.type === 'completed' && vertex.type === 'completed') {
                return !(
                  v.ringId === vertex.ringId && v.pointId === vertex.pointId
                );
              }
              return true;
            });
          }
          return [...prev, vertex];
        }

        // Regular click: clear all and select only this vertex
        return [vertex];
      });
    },
    []
  );

  const handleBoxPointerDown = useCallback((event: PointerEvent) => {
    if (event.button !== 0) {
      return;
    }
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }

    const { canvasX, canvasY } = getCanvasMousePosition(
      event.clientX,
      event.clientY
    );

    const selection: BoxSelection = {
      start: [canvasX, canvasY],
      end: [canvasX, canvasY],
    };

    boxSelectionRef.current = selection;
    setBoxSelection(selection);

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleBoxPointerMove = useCallback((event: PointerEvent) => {
    const current = boxSelectionRef.current;
    if (!current) {
      return;
    }

    const { canvasX, canvasY } = getCanvasMousePosition(
      event.clientX,
      event.clientY
    );

    const next: BoxSelection = {
      start: current.start,
      end: [canvasX, canvasY],
    };

    boxSelectionRef.current = next;
    setBoxSelection(next);

    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleBoxPointerUp = useCallback(
    (event: PointerEvent) => {
      const selection = boxSelectionRef.current;
      if (!selection) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const [x1, y1] = selection.start;
      const [x2, y2] = selection.end;
      const minX = Math.min(x1, x2);
      const maxX = Math.max(x1, x2);
      const minY = Math.min(y1, y2);
      const maxY = Math.max(y1, y2);

      // Ignore very small boxes (treat as click)
      if (Math.abs(maxX - minX) < 5 || Math.abs(maxY - minY) < 5) {
        boxSelectionRef.current = null;
        setBoxSelection(null);
        return;
      }

      const newSelection: SelectedVertex[] = [];

      state.incompleteRing.forEach(({ id, point }) => {
        const [vx, vy] = projectInDOM(point, viewer);
        if (vx >= minX && vx <= maxX && vy >= minY && vy <= maxY) {
          newSelection.push({ type: 'incomplete', pointId: id });
        }
      });

      state.completedRings.filter(Boolean).forEach((ring) => {
        ring.linearRing.forEach(({ id, point }, index) => {
          // Skip the duplicated last point that closes the ring
          if (index === ring.linearRing.length - 1) {
            return;
          }
          const [vx, vy] = projectInDOM(point, viewer);
          if (vx >= minX && vx <= maxX && vy >= minY && vy <= maxY) {
            newSelection.push({
              type: 'completed',
              ringId: ring.id,
              pointId: id,
            });
          }
        });
      });

      setSelectedVertices(newSelection);
      boxSelectionRef.current = null;
      setBoxSelection(null);
    },
    [state.completedRings, state.incompleteRing, viewer]
  );

  useEffect(() => {
    const container = viewer.container as HTMLElement | null;
    if (!container) {
      return;
    }

    container.addEventListener('pointerdown', handleBoxPointerDown);
    container.addEventListener('pointermove', handleBoxPointerMove);
    container.addEventListener('pointerup', handleBoxPointerUp);

    return () => {
      container.removeEventListener('pointerdown', handleBoxPointerDown);
      container.removeEventListener('pointermove', handleBoxPointerMove);
      container.removeEventListener('pointerup', handleBoxPointerUp);
    };
  }, [handleBoxPointerDown, handleBoxPointerMove, handleBoxPointerUp, viewer]);

  // Keep selection in sync with existing points so we never keep references
  // to vertices that no longer exist after edits like double-click removal.
  useEffect(() => {
    setSelectedVertices((prev) =>
      prev.filter((vertex) => {
        if (vertex.type === 'incomplete') {
          return state.incompleteRing.some(
            (point) => point.id === vertex.pointId
          );
        }
        const ring = state.completedRings.find((r) => r.id === vertex.ringId);
        if (!ring) {
          return false;
        }
        return ring.linearRing.some((point) => point.id === vertex.pointId);
      })
    );
  }, [state.completedRings, state.incompleteRing]);

  useHotkeys(
    ['backspace', 'del'],
    () => {
      setSelectedVertices((current) => {
        if (current.length === 0) {
          return current;
        }

        // Snapshot state once so the whole delete operation is a single undo step
        pushHistorySnapshot();

        current.forEach((vertex) => {
          if (vertex.type === 'incomplete') {
            dispatch({
              type: PolygonDrawingActionType.REMOVE_INCOMPLETE_RING_POINT,
              pointId: vertex.pointId,
            });
          } else {
            dispatch({
              type: PolygonDrawingActionType.REMOVE_COMPLETE_RING_POINT,
              ringId: vertex.ringId,
              pointId: vertex.pointId,
            });
          }
        });

        return [];
      });
    },
    {
      preventDefault: true,
    },
    [dispatch, pushHistorySnapshot]
  );

  const getPoint = useCallback(
    (event: MouseEvent): Point | null => {
      let point: Point;

      if (state.plane) {
        if (snapper.isSnapped()) {
          point = projectPointOnPlane(
            getSnapPoint(snapper, viewer),
            state.plane
          );
          snapper.indicator.render();
        } else {
          point = projectMouseOnPlane(
            event.clientX,
            event.clientY,
            state.plane,
            viewer
          );
        }
      } else if (snapper.isSnapped()) {
        point = getSnapPoint(snapper, viewer);
        snapper.indicator.render();
      } else {
        return null;
      }

      return point;
    },
    [snapper, state.plane, viewer]
  );

  return (
    <>
      {boxSelection && (
        <Box
          position="absolute"
          zIndex={2}
          pointerEvents="none"
          style={{
            left: Math.min(boxSelection.start[0], boxSelection.end[0]),
            top: Math.min(boxSelection.start[1], boxSelection.end[1]),
            width: Math.abs(boxSelection.start[0] - boxSelection.end[0]),
            height: Math.abs(boxSelection.start[1] - boxSelection.end[1]),
            border: '1px solid rgba(0, 136, 255, 0.8)',
            backgroundColor: 'rgba(0, 136, 255, 0.15)',
          }}
        />
      )}
      <CompletedRings
        state={state}
        dispatch={dispatch}
        getPoint={getPoint}
        isDragging={isDragging}
        setIsDragging={setIsDragging}
        isOneClickShapeMode={isOneClickShapeMode}
        viewer={viewer}
        snapper={snapper}
        thickness={thickness}
        selectedVertices={selectedVertices}
        onVertexSelectionClick={(ringId, pointId, isShiftPressed) =>
          handleVertexSelectionClick(
            { type: 'completed', ringId, pointId },
            isShiftPressed
          )
        }
        onBeforeGeometryChange={pushHistorySnapshot}
        historyVersion={historyVersion}
      />
      <IncompleteRing
        state={state}
        dispatch={dispatch}
        getPoint={getPoint}
        isDragging={isDragging}
        setIsDragging={setIsDragging}
        viewer={viewer}
        snapper={snapper}
        isOneClickShapeMode={isOneClickShapeMode}
        isRectangleMode={isRectangleMode ?? false}
        hiddenModelUrns={hiddenModelUrns}
        selectedCategories={selectedCategories}
        setIsCalculatingIntersections={setIsCalculatingIntersections}
        setCalculationClickPoint={setCalculationClickPoint}
        selectedVertices={selectedVertices}
        onVertexSelectionClick={(pointId, isShiftPressed) =>
          handleVertexSelectionClick(
            { type: 'incomplete', pointId },
            isShiftPressed
          )
        }
        onBeforeGeometryChange={pushHistorySnapshot}
        historyVersion={historyVersion}
      />
      {isCalculatingIntersections && calculationClickPoint && (
        <LoadingSpinner point={calculationClickPoint} viewer={viewer} />
      )}
    </>
  );
};

function LoadingSpinner({
  point,
  viewer,
}: {
  point: Point;
  viewer: Autodesk.Viewing.GuiViewer3D;
}) {
  const [cameraPosition, setCameraPosition] = useState({});
  const position2D = useMemo<Point2>(
    () => projectInDOM(point, viewer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [point, viewer, cameraPosition]
  );

  useCameraChangedEvent(
    viewer,
    useCallback(() => {
      setCameraPosition({});
    }, [])
  );

  return (
    <Box
      position="absolute"
      zIndex={1000}
      style={{
        left: position2D[0] - 12,
        top: position2D[1] - 12,
      }}
      width="24px"
      height="24px"
    >
      <Spinner size="md" color="teal.500" thickness="3px" />
    </Box>
  );
}

// eslint-disable-next-line complexity
function IncompleteRing({
  state,
  dispatch,
  getPoint,
  isDragging,
  setIsDragging,
  isOneClickShapeMode,
  isRectangleMode,
  viewer,
  snapper,
  hiddenModelUrns,
  selectedCategories,
  setIsCalculatingIntersections,
  setCalculationClickPoint,
  selectedVertices,
  onVertexSelectionClick,
  onBeforeGeometryChange,
  historyVersion,
}: {
  state: PolygonDrawingState;
  dispatch: Dispatch<PolygonDrawingAction>;
  getPoint: (event: MouseEvent) => Point | null;
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  isOneClickShapeMode: boolean;
  isRectangleMode: boolean;
  viewer: Autodesk.Viewing.GuiViewer3D;
  snapper: Autodesk.Viewing.Extensions.Snapping.Snapper;
  hiddenModelUrns: string[];
  selectedCategories?: string[];
  setIsCalculatingIntersections: (value: boolean) => void;
  setCalculationClickPoint: (point: Point | null) => void;
  selectedVertices: SelectedVertex[];
  onVertexSelectionClick: (pointId: string, isShiftPressed: boolean) => void;
  onBeforeGeometryChange: () => void;
  historyVersion: number;
}) {
  // To avoid triggering click events when double clicking
  const clickTimeoutRef = useRef<number>();
  // Store principal axes for rectangle mode
  const principalAxesRef = useRef<{ axis1: Point2; axis2: Point2 } | null>(
    null
  );

  // State for tracking drag feedback in incomplete ring
  const [dragFeedback, setDragFeedback] = useState<{
    type: 'vertex' | 'edge';
    originPoint: Point;
    currentPoint: Point;
    pointId: string;
    direction: Point;
  } | null>(null);

  // Clear drag feedback when undo/redo occurs
  useEffect(() => {
    setDragFeedback(null);
  }, [historyVersion]);

  // Refs for tracking drag origin
  const vertexDragOriginRef = useRef<Point | null>(null);
  const edgeDragOriginRef = useRef<Point | null>(null);

  const { loadedModels } = useViewer();
  const unitScale = useMemo(
    () => first(Object.values(loadedModels))?.getUnitScale() ?? 1,
    [loadedModels]
  );

  const edgesForIncompleteRing = useMemo(
    () => generateIdsForEdges(state.incompleteRing),
    [state.incompleteRing]
  );
  const edgesForIncompleteRing2 = useMemo(
    () => generateIdsForEdges(state.incompleteRing2),
    [state.incompleteRing2]
  );

  const enableMagicShapeIntersections = useFlag(
    'enable-magic-shape-intersections'
  );

  const shouldAddMousePointerEdge =
    !!state.mousePointerPoint && state.incompleteRing.length > 0;

  const intersectionsForIncompleteRingIncludingMouseEdge = useMemo(() => {
    let result = state.intersectionsForIncompleteRing;
    if (state.plane && state.mousePointerPoint && shouldAddMousePointerEdge) {
      // Find intersections with the edge between the last confirmed point and the mouse edge
      result = copy(result);
      const intermediateEdge = createIntermediateEdge(
        state.incompleteRing2,
        toPlanarPoint(state.mousePointerPoint, state.plane)
      );
      addEdge(result, edgesForIncompleteRing2, intermediateEdge);
    }
    return result;
  }, [
    edgesForIncompleteRing2,
    shouldAddMousePointerEdge,
    state.incompleteRing2,
    state.intersectionsForIncompleteRing,
    state.mousePointerPoint,
    state.plane,
  ]);

  const invalidEdgesForIncompleteRing = useMemo(() => {
    return getInvalidEdges(
      edgesForIncompleteRing2,
      intersectionsForIncompleteRingIncludingMouseEdge
    );
  }, [
    edgesForIncompleteRing2,
    intersectionsForIncompleteRingIncludingMouseEdge,
  ]);

  const doesIncompleteRingFormAValidPolygon = useMemo(() => {
    // Valid if we have 2 points and a mouse pointer point, or if we have 3 points

    const numPoints = state.mousePointerPoint
      ? state.incompleteRing2.length + 1
      : state.incompleteRing2.length;

    if (numPoints < 3) {
      return false;
    }
    if (invalidEdgesForIncompleteRing.length > 0) {
      return false;
    }
    if (!state.plane) {
      return false;
    }
    if (state.mousePointerPoint) {
      // check intersection from the mouse pointer point to the first point
      const edgeFromMousePoint: LineSegment2 = [
        toPlanarPoint(state.mousePointerPoint, state.plane),
        state.incompleteRing2[0].point,
      ];
      return !edgesForIncompleteRing2.some((edge) =>
        intersects(edgeFromMousePoint, edge.segment)
      );
    } else {
      // check intersection from the last point to the first point
      const edgeFromLastPoint: LineSegment2 = [
        state.incompleteRing2[state.incompleteRing2.length - 1].point,
        state.incompleteRing2[0].point,
      ];
      return !edgesForIncompleteRing2.some((edge) =>
        intersects(edgeFromLastPoint, edge.segment)
      );
    }
  }, [
    edgesForIncompleteRing2,
    invalidEdgesForIncompleteRing.length,
    state.incompleteRing2,
    state.mousePointerPoint,
    state.plane,
  ]);

  const isAltKeyPressed = isHotkeyPressed('alt');

  const handleViewerClick = useCallback(
    // eslint-disable-next-line complexity
    (event: MouseEvent): boolean => {
      if (!isPrimaryMouseButton(event.button)) {
        return false;
      }
      if (isOneClickShapeMode) {
        const oneClickShape = getShape(viewer, event);

        if (oneClickShape) {
          // Snapshot before applying magic shape so undo restores pre-click state
          onBeforeGeometryChange();

          if (enableMagicShapeIntersections) {
            // Get the 3D point where the user clicked
            const clickPoint = projectMouseOnPlane(
              event.clientX,
              event.clientY,
              oneClickShape.plane,
              viewer
            );

            setIsCalculatingIntersections(true);
            setCalculationClickPoint(clickPoint);

            getRingsFromIntersections(
              oneClickShape,
              viewer,
              hiddenModelUrns,
              selectedCategories
            )
              .then((ringsFromIntersections) => {
                dispatch({
                  type: PolygonDrawingActionType.ADD_ONE_CLICK_SHAPE,
                  rings: ringsFromIntersections
                    ? ringsFromIntersections
                    : oneClickShape.rings,
                  plane: oneClickShape.plane,
                });
              })
              .catch(() => {
                console.warn('Failed to get ring from intersections');
              })
              .finally(() => {
                setIsCalculatingIntersections(false);
                setCalculationClickPoint(null);
              });
          } else {
            dispatch({
              type: PolygonDrawingActionType.ADD_ONE_CLICK_SHAPE,
              rings: oneClickShape.rings,
              plane: oneClickShape.plane,
            });
          }
        }

        return true;
      }

      if (isRectangleMode) {
        if (state.incompleteRing.length === 0) {
          // First click: use one-click shape to get connected plane polygon and principal axes
          const magicShape = getShape(viewer, event);
          if (!magicShape) {
            return true;
          }

          const { rings, plane } = magicShape;

          // Collect all planar points from the merged polygon
          const planarPoints: Point2[] = [];
          for (const ring of rings) {
            const ring2d = toPlanarPoints(ring, plane);
            planarPoints.push(...ring2d);
          }

          if (planarPoints.length < 3) {
            return true;
          }

          // Calculate principal axes once per rectangle based on the connected polygon
          const principalAxes = calculatePrincipalAxes(planarPoints);
          principalAxesRef.current = principalAxes;

          // Snapshot before applying rectangle
          onBeforeGeometryChange();

          // Set plane first
          dispatch({
            type: PolygonDrawingActionType.SET_PLANE,
            plane,
          });

          // Add first point at the click position
          const firstPoint = projectMouseOnPlane(
            event.clientX,
            event.clientY,
            plane,
            viewer
          );
          dispatch({
            type: PolygonDrawingActionType.ADD_INCOMPLETE_RING_POINT,
            position: firstPoint,
            isAltKeyPressed: false,
          });
        } else if (state.incompleteRing.length === 1 && state.plane) {
          // Second click: complete rectangle
          const newPoint = getPoint(event);
          if (newPoint && principalAxesRef.current) {
            // Snapshot before completing rectangle
            onBeforeGeometryChange();
            const firstPoint = state.incompleteRing[0].point;
            const firstPoint2 = toPlanarPoint(firstPoint, state.plane);
            const newPoint2 = toPlanarPoint(newPoint, state.plane);

            // Calculate rectangle corners aligned to principal axes
            const { axis1, axis2 } = principalAxesRef.current;

            // Project first and second points onto principal axes
            const diff: Point2 = [
              newPoint2[0] - firstPoint2[0],
              newPoint2[1] - firstPoint2[1],
            ];
            const proj1 = diff[0] * axis1[0] + diff[1] * axis1[1];
            const proj2 = diff[0] * axis2[0] + diff[1] * axis2[1];

            // Calculate all four corners
            const corner2: Point2 = [
              firstPoint2[0] + proj1 * axis1[0],
              firstPoint2[1] + proj1 * axis1[1],
            ];
            const corner3: Point2 = [
              corner2[0] + proj2 * axis2[0],
              corner2[1] + proj2 * axis2[1],
            ];
            const corner4: Point2 = [
              firstPoint2[0] + proj2 * axis2[0],
              firstPoint2[1] + proj2 * axis2[1],
            ];

            // Convert back to 3D and add points
            const corner2_3d = to3dPoints([corner2], state.plane)[0];
            const corner3_3d = to3dPoints([corner3], state.plane)[0];
            const corner4_3d = to3dPoints([corner4], state.plane)[0];

            dispatch({
              type: PolygonDrawingActionType.ADD_INCOMPLETE_RING_POINT,
              position: corner2_3d,
              isAltKeyPressed: false,
            });
            dispatch({
              type: PolygonDrawingActionType.ADD_INCOMPLETE_RING_POINT,
              position: corner3_3d,
              isAltKeyPressed: false,
            });
            dispatch({
              type: PolygonDrawingActionType.ADD_INCOMPLETE_RING_POINT,
              position: corner4_3d,
              isAltKeyPressed: false,
            });
            // Close the ring
            dispatch({ type: PolygonDrawingActionType.CLOSE_RING });
          }
        }
        return true;
      }

      const newPoint = getPoint(event);
      if (newPoint) {
        onBeforeGeometryChange();
        dispatch({
          type: PolygonDrawingActionType.ADD_INCOMPLETE_RING_POINT,
          position: newPoint,
          isAltKeyPressed,
        });
      }

      return true; // Stop the event from going to other tools in the stack
    },
    [
      isOneClickShapeMode,
      isRectangleMode,
      getPoint,
      viewer,
      enableMagicShapeIntersections,
      hiddenModelUrns,
      selectedCategories,
      dispatch,
      isAltKeyPressed,
      setIsCalculatingIntersections,
      setCalculationClickPoint,
      state.incompleteRing,
      state.plane,
      principalAxesRef,
      onBeforeGeometryChange,
    ]
  );

  useViewerClickEvent(viewer, handleViewerClick);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      snapper.indicator.clearOverlays();
      snapper.indicator.render();
      if (isOneClickShapeMode) {
        dispatch({
          type: PolygonDrawingActionType.SET_MOUSE_POINTER_POINT,
          point: null,
        });
        return;
      }

      if (state.incompleteRing.length === 0 || isDragging) {
        return;
      }
      const newPoint = getPoint(event);
      dispatch({
        type: PolygonDrawingActionType.SET_MOUSE_POINTER_POINT,
        point: newPoint,
      });
    },
    [
      dispatch,
      getPoint,
      isDragging,
      isOneClickShapeMode,
      snapper.indicator,
      state.incompleteRing.length,
    ]
  );

  useEffect(() => {
    viewer.container.addEventListener('pointermove', handlePointerMove);
    return () => {
      viewer.container.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove, snapper.indicator, viewer]);

  const handleVertexDrag = (
    gestureState: { event: MouseEvent; first: boolean; last: boolean },
    pointId: string
  ) => {
    const { event, first, last } = gestureState;
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    if (first) {
      onBeforeGeometryChange();
      // Capture original position before drag
      const vertex = state.incompleteRing.find((p) => p.id === pointId);
      if (vertex) {
        vertexDragOriginRef.current = vertex.point;
      }
    }
    const { canvasX, canvasY } = getCanvasMousePosition(
      event.clientX,
      event.clientY
    );
    snapper.onMouseMove({
      x: canvasX,
      y: canvasY,
    });
    snapper.indicator.render();
    const position = getPoint(event);
    if (!position) {
      return;
    }
    setIsDragging(true);

    // Update drag feedback state
    if (vertexDragOriginRef.current) {
      const direction = math.subtract(
        position,
        vertexDragOriginRef.current
      ) as Point;
      const length = math.norm(direction) as number;
      const normalizedDirection =
        length > 0
          ? (math.divide(direction, length) as Point)
          : ([0, 0, 0] as Point);

      setDragFeedback({
        type: 'vertex' as const,
        originPoint: vertexDragOriginRef.current,
        currentPoint: position,
        pointId,
        direction: normalizedDirection,
      });
    }

    dispatch({
      type: PolygonDrawingActionType.MOVE_INCOMPLETE_RING_POINT,
      position,
      pointId,
    });
    if (last) {
      setIsDragging(false);
    }
  };

  const handleEdgeDrag = (
    gestureState: { event: React.MouseEvent; first: boolean; last: boolean },
    edgeId: string
  ) => {
    const { event, first, last } = gestureState;
    if (!state.plane) {
      return;
    }

    const edgeIndex = edgesForIncompleteRing.findIndex(
      (edge) => edge.id === edgeId
    );
    const startPointId = state.incompleteRing[edgeIndex].id;

    if (first) {
      onBeforeGeometryChange();
      // Capture original edge midpoint before drag
      const startPoint = state.incompleteRing[edgeIndex].point;
      const endPoint =
        state.incompleteRing[(edgeIndex + 1) % state.incompleteRing.length]
          .point;
      edgeDragOriginRef.current = math.multiply(
        0.5,
        math.add(startPoint, endPoint)
      ) as Point;
    }
    setIsDragging(true);
    const startVector = projectMouseOnPlane(
      event.clientX - event.movementX,
      event.clientY - event.movementY,
      state.plane,
      viewer
    );
    const translationVector = math.subtract(
      projectMouseOnPlane(event.clientX, event.clientY, state.plane, viewer),
      startVector
    );

    // Calculate current edge midpoint for feedback
    const startPoint = state.incompleteRing[edgeIndex].point;
    const endPoint =
      state.incompleteRing[(edgeIndex + 1) % state.incompleteRing.length].point;
    const currentMidpoint = math.multiply(
      0.5,
      math.add(
        math.add(startPoint, translationVector),
        math.add(endPoint, translationVector)
      )
    ) as Point;

    // Update drag feedback state
    if (edgeDragOriginRef.current) {
      const direction = math.subtract(
        currentMidpoint,
        edgeDragOriginRef.current
      ) as Point;
      const length = math.norm(direction) as number;
      const normalizedDirection =
        length > 0
          ? (math.divide(direction, length) as Point)
          : ([0, 0, 0] as Point);

      setDragFeedback({
        type: 'edge',
        originPoint: edgeDragOriginRef.current,
        currentPoint: currentMidpoint,
        pointId: startPointId,
        direction: normalizedDirection,
      });
    }

    dispatch({
      type: PolygonDrawingActionType.MOVE_INCOMPLETE_RING_EDGE,
      startPositionTranslation: translationVector,
      endPositionTranslation: translationVector,
      startPointId,
    });

    if (last) {
      setIsDragging(false);
    }
  };

  const handleFirstVertexClick = (event: React.MouseEvent) => {
    const handleFirstVertexClickThrottled = () => {
      if (
        !state.plane ||
        state.incompleteRing.length < 3 ||
        event.button !== 0 ||
        event.detail > 1
      ) {
        return;
      }
      dispatch({ type: PolygonDrawingActionType.CLOSE_RING });
    };

    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current); // Clear any existing timeout
    }
    // Wait before executing single click action
    const newTimeout = window.setTimeout(handleFirstVertexClickThrottled, 300);

    clickTimeoutRef.current = newTimeout;
  };

  const handleVertexDoubleClick = (
    event: React.MouseEvent,
    pointId: string
  ) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current); // Clear the single click timeout
    }
    if (event.button !== 0) {
      return;
    }
    onBeforeGeometryChange();
    dispatch({
      type: PolygonDrawingActionType.REMOVE_INCOMPLETE_RING_POINT,
      pointId,
    });
  };

  const handleEdgeDoubleClick = (event: React.MouseEvent, edgeId: string) => {
    if (event.button !== 0) {
      return;
    }
    const edgeIndex = edgesForIncompleteRing.findIndex(
      (edge) => edge.id === edgeId
    );
    const startPointId = state.incompleteRing[edgeIndex].id;
    const newPoint = getPositionOnEdge(
      event,
      edgesForIncompleteRing[edgeIndex].segment,
      viewer
    );

    onBeforeGeometryChange();
    dispatch({
      type: PolygonDrawingActionType.SPLIT_INCOMPLETE_RING_EDGE,
      splitPosition: newPoint,
      startPointId,
    });
  };

  const handleIntermediateLengthSubmit = useCallback(
    (lengthInMeters: number) => {
      if (
        state.incompleteRing.length === 0 ||
        !state.mousePointerPoint ||
        lengthInMeters <= 0
      ) {
        return;
      }

      const lastPoint =
        state.incompleteRing[state.incompleteRing.length - 1].point;
      const mousePoint = state.mousePointerPoint;

      // Calculate direction vector from last point to mouse point
      const direction = math.subtract(mousePoint, lastPoint) as Point;
      const currentLength = math.norm(direction) as number;

      if (currentLength === 0) {
        return;
      }

      // Normalize the direction
      const normalizedDirection = math.divide(
        direction,
        currentLength
      ) as Point;

      // Convert the submitted length from meters to model units
      const lengthInModelUnits = lengthInMeters / unitScale;

      // Calculate the new point position
      const newPoint = math.add(
        lastPoint,
        math.multiply(normalizedDirection, lengthInModelUnits)
      ) as Point;

      onBeforeGeometryChange();
      dispatch({
        type: PolygonDrawingActionType.ADD_INCOMPLETE_RING_POINT,
        position: newPoint,
        isAltKeyPressed: false,
      });
    },
    [
      state.incompleteRing,
      state.mousePointerPoint,
      unitScale,
      onBeforeGeometryChange,
      dispatch,
    ]
  );

  useHotkeys(
    ['enter'],
    () => {
      onBeforeGeometryChange();
      dispatch({ type: PolygonDrawingActionType.CLOSE_RING });
    },
    {
      preventDefault: true,
    }
  );

  useHotkeys(
    ['tab'],
    () => {
      onBeforeGeometryChange();
      dispatch({ type: PolygonDrawingActionType.FLIP_PLANE_NORMAL });
    },
    {
      preventDefault: true,
    }
  );

  const isIntermediateEdgeValid = useMemo(() => {
    const intersectionsForEdge =
      intersectionsForIncompleteRingIncludingMouseEdge.get(intermediateEdgeId);
    if (!intersectionsForEdge) {
      return true;
    }
    return intersectionsForEdge.length === 0;
  }, [intersectionsForIncompleteRingIncludingMouseEdge]);

  // Handler for when user submits a length after dragging in incomplete ring
  const handleDragLengthSubmit = useCallback(
    (lengthInMeters: number) => {
      if (!dragFeedback || lengthInMeters <= 0) {
        return;
      }

      // Convert the submitted length from meters to model units
      const lengthInModelUnits = lengthInMeters / unitScale;

      // Calculate new position based on direction and submitted length
      const newPosition = math.add(
        dragFeedback.originPoint,
        math.multiply(dragFeedback.direction, lengthInModelUnits)
      ) as Point;

      onBeforeGeometryChange();

      if (dragFeedback.type === 'vertex') {
        dispatch({
          type: PolygonDrawingActionType.MOVE_INCOMPLETE_RING_POINT,
          position: newPosition,
          pointId: dragFeedback.pointId,
        });
      } else {
        // For edges, calculate the translation vector
        const translation = math.subtract(
          newPosition,
          dragFeedback.currentPoint
        ) as Point;

        dispatch({
          type: PolygonDrawingActionType.MOVE_INCOMPLETE_RING_EDGE,
          startPositionTranslation: translation,
          endPositionTranslation: translation,
          startPointId: dragFeedback.pointId,
        });
      }

      // Keep the feedback visible with updated position for subsequent edits
      setDragFeedback({
        ...dragFeedback,
        currentPoint: newPosition,
      });
    },
    [dragFeedback, unitScale, onBeforeGeometryChange, dispatch]
  );

  const rectanglePreview: MultiPolygon | null = useMemo(() => {
    if (
      !isRectangleMode ||
      state.incompleteRing.length !== 1 ||
      !state.mousePointerPoint ||
      !state.plane ||
      !principalAxesRef.current
    ) {
      return null;
    }

    const firstPoint = state.incompleteRing[0].point;
    const firstPoint2 = toPlanarPoint(firstPoint, state.plane);
    const mousePoint2 = toPlanarPoint(state.mousePointerPoint, state.plane);

    // Calculate rectangle corners aligned to principal axes
    const { axis1, axis2 } = principalAxesRef.current;

    // Project mouse point relative to first point onto principal axes
    const diff: Point2 = [
      mousePoint2[0] - firstPoint2[0],
      mousePoint2[1] - firstPoint2[1],
    ];
    const proj1 = diff[0] * axis1[0] + diff[1] * axis1[1];
    const proj2 = diff[0] * axis2[0] + diff[1] * axis2[1];

    // Calculate all four corners
    const corner1: Point2 = firstPoint2;
    const corner2: Point2 = [
      firstPoint2[0] + proj1 * axis1[0],
      firstPoint2[1] + proj1 * axis1[1],
    ];
    const corner3: Point2 = [
      corner2[0] + proj2 * axis2[0],
      corner2[1] + proj2 * axis2[1],
    ];
    const corner4: Point2 = [
      firstPoint2[0] + proj2 * axis2[0],
      firstPoint2[1] + proj2 * axis2[1],
    ];

    // Convert back to 3D and ensure ring is closed by repeating the first point
    const exterior3 = to3dPoints(
      [corner1, corner2, corner3, corner4, corner1],
      state.plane
    );
    return {
      polygons: [
        {
          exterior: exterior3,
          interiors: [],
        },
      ],
    };
  }, [
    isRectangleMode,
    state.incompleteRing,
    state.mousePointerPoint,
    state.plane,
  ]);

  const incompletePolygon: MultiPolygon | null = useMemo(() => {
    if (isRectangleMode && rectanglePreview) {
      return null; // Use rectangle preview instead
    }
    if (!doesIncompleteRingFormAValidPolygon || !state.plane) {
      return null;
    }
    const exterior = getPositions(state.incompleteRing2);
    if (shouldAddMousePointerEdge && state.mousePointerPoint) {
      const mousePointerPoint2 = toPlanarPoint(
        state.mousePointerPoint,
        state.plane
      );
      exterior.push(mousePointerPoint2);
    }
    exterior.push(state.incompleteRing2[0].point);
    const exterior3 = to3dPoints(exterior, state.plane);
    return {
      polygons: [
        {
          exterior: exterior3,
          interiors: [],
        },
      ],
    };
  }, [
    isRectangleMode,
    rectanglePreview,
    doesIncompleteRingFormAValidPolygon,
    shouldAddMousePointerEdge,
    state.incompleteRing2,
    state.mousePointerPoint,
    state.plane,
  ]);
  return (
    <>
      {rectanglePreview && state.plane && (
        <Polygon
          multipolygon={rectanglePreview}
          plane={state.plane}
          viewer={viewer}
          isClosed={false}
        />
      )}
      {incompletePolygon && state.plane && !isAltKeyPressed && (
        <Polygon
          multipolygon={incompletePolygon}
          plane={state.plane}
          viewer={viewer}
          isClosed={false}
        />
      )}
      {edgesForIncompleteRing.map(({ id, segment }, idx) => {
        const color = getEdgeColor(
          false,
          !invalidEdgesForIncompleteRing.includes(id)
        );
        if (idx === edgesForIncompleteRing.length - 1 && isAltKeyPressed)
          return;
        return (
          <DraggableEdge
            key={id}
            onDoubleClick={(event) => handleEdgeDoubleClick(event, id)}
            segment={segment}
            backgroundColor={color}
            labelColorScheme={getColorSchemeFromColor(color)}
            viewer={viewer}
            onEdgeDrag={(gestureState) => handleEdgeDrag(gestureState, id)}
          />
        );
      })}
      {isRectangleMode &&
      state.incompleteRing.length === 1 &&
      state.mousePointerPoint &&
      state.plane &&
      principalAxesRef.current
        ? (() => {
            const firstPoint = state.incompleteRing[0].point;
            const firstPoint2 = toPlanarPoint(firstPoint, state.plane);
            const mousePoint2 = toPlanarPoint(
              state.mousePointerPoint,
              state.plane
            );
            const { axis1, axis2 } = principalAxesRef.current;

            const diff: Point2 = [
              mousePoint2[0] - firstPoint2[0],
              mousePoint2[1] - firstPoint2[1],
            ];
            const proj1 = diff[0] * axis1[0] + diff[1] * axis1[1];
            const proj2 = diff[0] * axis2[0] + diff[1] * axis2[1];

            const corner2_2d: Point2 = [
              firstPoint2[0] + proj1 * axis1[0],
              firstPoint2[1] + proj1 * axis1[1],
            ];
            const corner3_2d: Point2 = [
              corner2_2d[0] + proj2 * axis2[0],
              corner2_2d[1] + proj2 * axis2[1],
            ];
            const corner4_2d: Point2 = [
              firstPoint2[0] + proj2 * axis2[0],
              firstPoint2[1] + proj2 * axis2[1],
            ];

            const corner2 = to3dPoints([corner2_2d], state.plane)[0];
            const corner3 = to3dPoints([corner3_2d], state.plane)[0];
            const corner4 = to3dPoints([corner4_2d], state.plane)[0];

            const previewColor = getEdgeColor(false, true);

            return (
              <>
                {/* Preview rectangle edges, non-interactive */}
                <Edge
                  segment={[firstPoint, corner2]}
                  backgroundColor={previewColor}
                  labelColorScheme={getColorSchemeFromColor(previewColor)}
                  viewer={viewer}
                  pointerEvents="none"
                />
                <Edge
                  segment={[corner2, corner3]}
                  backgroundColor={previewColor}
                  labelColorScheme={getColorSchemeFromColor(previewColor)}
                  viewer={viewer}
                  pointerEvents="none"
                />
                <Edge
                  segment={[corner3, corner4]}
                  backgroundColor={previewColor}
                  labelColorScheme={getColorSchemeFromColor(previewColor)}
                  viewer={viewer}
                  pointerEvents="none"
                />
                <Edge
                  segment={[corner4, firstPoint]}
                  backgroundColor={previewColor}
                  labelColorScheme={getColorSchemeFromColor(previewColor)}
                  viewer={viewer}
                  pointerEvents="none"
                />

                {/* Preview vertices, non-interactive */}
                <Vertex
                  theme="inverted"
                  point={firstPoint}
                  viewer={viewer}
                  pointerEvents="none"
                />
                <Vertex
                  theme="default"
                  point={corner2}
                  viewer={viewer}
                  pointerEvents="none"
                />
                <Vertex
                  theme="default"
                  point={corner3}
                  viewer={viewer}
                  pointerEvents="none"
                />
                <Vertex
                  theme="default"
                  point={corner4}
                  viewer={viewer}
                  pointerEvents="none"
                />
              </>
            );
          })()
        : shouldAddMousePointerEdge
        ? IntermediateEdges(
            state.mousePointerPoint as Point,
            state.incompleteRing,
            state.plane,
            isAltKeyPressed && state.incompleteRing.length > 1,
            viewer,
            getEdgeColor(false, isIntermediateEdgeValid),
            true, // showLengthLabel
            true, // isEditable
            handleIntermediateLengthSubmit
          )
        : null}
      {!isRectangleMode &&
        state.incompleteRing.map(({ point, id: pointId }, index) => {
          return (
            <DraggableVertex
              key={pointId}
              isSelected={selectedVertices.some(
                (vertex) =>
                  vertex.type === 'incomplete' && vertex.pointId === pointId
              )}
              theme={index === 0 ? 'inverted' : 'default'}
              point={point}
              viewer={viewer}
              onClick={
                index === 0
                  ? (event) => handleFirstVertexClick(event)
                  : undefined
              }
              onDoubleClick={(event) => handleVertexDoubleClick(event, pointId)}
              onVertexDrag={(gestureState) =>
                handleVertexDrag(gestureState, pointId)
              }
            />
          );
        })}

      {/* Render drag feedback line when dragging or after drag completes */}
      {dragFeedback && (
        <DragFeedbackLine
          origin={dragFeedback.originPoint}
          current={dragFeedback.currentPoint}
          viewer={viewer}
          isEditable={!isDragging}
          isDragging={isDragging}
          onLengthSubmit={handleDragLengthSubmit}
        />
      )}
    </>
  );
}

export const IntermediateEdges = (
  intermediatePoint: Point,
  ring: Point3WithId[],
  plane: Plane | null,
  isArc: boolean,
  viewer: Autodesk.Viewing.Viewer3D,
  color: string,
  showLengthLabel = false,
  isEditable = false,
  onLengthSubmit?: (newLengthInMeters: number) => void
) => {
  if (isArc) {
    const intermediatePlane = plane
      ? plane
      : calculatePlaneEquation([
          ring[ring.length - 2].point,
          ring[ring.length - 1].point,
          intermediatePoint,
        ]);

    const startPoint2 = toPlanarPoint(
      ring[ring.length - 2].point,
      intermediatePlane
    );
    const endPoint2 = toPlanarPoint(
      ring[ring.length - 1].point,
      intermediatePlane
    );
    const intermediatePoint2 = toPlanarPoint(
      intermediatePoint,
      intermediatePlane
    );

    const arcPoints = to3dPoints(
      getArcPoints(startPoint2, endPoint2, intermediatePoint2),
      intermediatePlane
    );

    return arcPoints
      .slice(1)
      .map((point, index) => (
        <Edge
          key={index}
          segment={[arcPoints[index], point]}
          backgroundColor={color}
          labelColorScheme={getColorSchemeFromColor(color)}
          pointerEvents="none"
          viewer={viewer}
          showLengthLabel={true}
        />
      ));
  }

  const segment: LineSegment = [ring[ring.length - 1].point, intermediatePoint];

  return (
    <>
      <Edge
        segment={segment}
        backgroundColor={color}
        labelColorScheme={getColorSchemeFromColor(color)}
        viewer={viewer}
        pointerEvents="none"
        showLengthLabel={!showLengthLabel} // Hide Edge's label if we're showing the editable one
      />
      {showLengthLabel && (
        <EditableLengthLabel
          segment={segment}
          viewer={viewer}
          isEditable={isEditable}
          onLengthSubmit={onLengthSubmit}
        />
      )}
    </>
  );
};

function CompletedRings({
  state,
  dispatch,
  getPoint,
  isDragging,
  setIsDragging,
  isOneClickShapeMode,
  viewer,
  snapper,
  thickness,
  selectedVertices,
  onVertexSelectionClick,
  onBeforeGeometryChange,
  historyVersion,
}: {
  state: PolygonDrawingState;
  dispatch: Dispatch<PolygonDrawingAction>;
  getPoint: (event: MouseEvent) => Point | null;
  isDragging: boolean;
  setIsDragging: (isDragging: boolean) => void;
  isOneClickShapeMode: boolean;
  viewer: Autodesk.Viewing.GuiViewer3D;
  snapper: Autodesk.Viewing.Extensions.Snapping.Snapper;
  thickness?: number;
  selectedVertices: SelectedVertex[];
  onVertexSelectionClick: (
    ringId: string,
    pointId: string,
    isShiftPressed: boolean
  ) => void;
  onBeforeGeometryChange: () => void;
  historyVersion: number;
}) {
  const isShiftPressed = isHotkeyPressed('shift');

  // State for tracking drag feedback (shows faint line from origin to current position)
  const [dragFeedback, setDragFeedback] = useState<{
    type: 'vertex' | 'edge';
    originPoint: Point;
    currentPoint: Point;
    ringId: string;
    pointId: string;
    direction: Point; // Normalized direction of the drag
  } | null>(null);

  // Clear drag feedback when undo/redo occurs
  useEffect(() => {
    setDragFeedback(null);
  }, [historyVersion]);

  const { loadedModels } = useViewer();
  const unitScale = useMemo(
    () => first(Object.values(loadedModels))?.getUnitScale() ?? 1,
    [loadedModels]
  );

  const edgesForCompletedRings = useMemo(
    () =>
      Object.fromEntries(
        state.completedRings
          .filter(Boolean)
          .map((ring) => [ring.id, generateIdsForEdges(ring.linearRing)])
      ),
    [state.completedRings]
  );

  const invalidEdgesForCompletedRings = useMemo(() => {
    return getInvalidEdges(
      Object.values(edgesForCompletedRings).flat(),
      state.intersectionsForCompletedRings
    );
  }, [edgesForCompletedRings, state.intersectionsForCompletedRings]);

  const completedMultipolygon: MultiPolygon | null = useMemo(() => {
    if (state.completedRings2.length === 0 || !state.plane) {
      return null;
    }
    const rings = state.completedRings2.map((ring) => ring.linearRing);
    // rings without intersections
    const validRings = rings
      .filter((ring) => {
        const edges = generateIdsForEdges(ring);
        return (
          getInvalidEdges(edges, state.intersectionsForCompletedRings)
            .length === 0
        );
      })
      .map((ring) => getPositions(ring));
    return to3dMultiPolygon(createMultipolygon(validRings), state.plane);
  }, [
    state.completedRings2,
    state.intersectionsForCompletedRings,
    state.plane,
  ]);

  // Ref to track the original vertex position when drag starts
  const vertexDragOriginRef = useRef<Point | null>(null);

  const handleVertexDrag = (
    gestureState: { event: MouseEvent; first: boolean; last: boolean },
    pointId: string,
    ringId: string
  ) => {
    const { event, first, last } = gestureState;
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    if (first) {
      onBeforeGeometryChange();
      // Capture original position before drag
      const ring = state.completedRings.find((r) => r.id === ringId);
      const vertex = ring?.linearRing.find((p) => p.id === pointId);
      if (vertex) {
        vertexDragOriginRef.current = vertex.point;
      }
    }
    setIsDragging(true);
    const { canvasX, canvasY } = getCanvasMousePosition(
      event.clientX,
      event.clientY
    );
    snapper.onMouseMove({
      x: canvasX,
      y: canvasY,
    });
    snapper.indicator.render();
    const position = getPoint(event);

    if (!position) {
      return;
    }

    // Update drag feedback state
    if (vertexDragOriginRef.current) {
      const direction = math.subtract(
        position,
        vertexDragOriginRef.current
      ) as Point;
      const length = math.norm(direction) as number;
      const normalizedDirection =
        length > 0
          ? (math.divide(direction, length) as Point)
          : ([0, 0, 0] as Point);

      setDragFeedback({
        type: 'vertex' as const,
        originPoint: vertexDragOriginRef.current,
        currentPoint: position,
        ringId,
        pointId,
        direction: normalizedDirection,
      });
    }

    dispatch({
      type: PolygonDrawingActionType.MOVE_COMPLETE_RING_POINT,
      position,
      pointId,
      ringId,
    });
    if (last) {
      setIsDragging(false);
      // Keep the drag feedback visible after drag ends
      // It will be cleared when a new drag starts or the next point is placed
    }
  };

  // Ref to track the original edge midpoint when drag starts
  const edgeDragOriginRef = useRef<Point | null>(null);

  const handleEdgeDrag = (
    gestureState: { event: React.MouseEvent; first: boolean; last: boolean },
    edgeId: string,
    ringId: string
  ) => {
    const { event, first, last } = gestureState;
    if (!state.plane) {
      return;
    }

    const edgeIndex = edgesForCompletedRings[ringId].findIndex(
      (edge) => edge.id === edgeId
    );
    const ring = state.completedRings.find((r) => r.id === ringId)?.linearRing;
    const startPointId = ring?.[edgeIndex]?.id;

    invariant(startPointId, 'start point not found');
    invariant(ring, 'ring not found');

    if (first) {
      onBeforeGeometryChange();
      // Capture original edge midpoint before drag
      const startPoint = ring[edgeIndex].point;
      const endPoint = ring[(edgeIndex + 1) % ring.length].point;
      edgeDragOriginRef.current = math.multiply(
        0.5,
        math.add(startPoint, endPoint)
      ) as Point;
    }
    setIsDragging(true);
    const startVector = projectMouseOnPlane(
      event.clientX - event.movementX,
      event.clientY - event.movementY,
      state.plane,
      viewer
    );

    const draggingVector = math.subtract(
      projectMouseOnPlane(event.clientX, event.clientY, state.plane, viewer),
      startVector
    );

    let startPositionTranslation: Point;
    let endPositionTranslation: Point;

    const newRing = [...ring];
    const startPointIndex = newRing.findIndex(
      (point) => point.id === startPointId
    );

    // Get the start and end points of the dragged edge
    const startPoint = newRing[startPointIndex].point;
    const endPoint = newRing[(startPointIndex + 1) % newRing.length].point;

    if (!isShiftPressed) {
      // Get points connected to the start and end points
      const prevPoint =
        newRing[
          startPointIndex - 1 < 0 ? newRing.length - 2 : startPointIndex - 1
        ].point;
      const nextPoint =
        newRing[
          startPointIndex + 2 > newRing.length - 1 ? 1 : startPointIndex + 2
        ].point;

      // Calculate projection of draggingVector onto the lines defined by connected points
      startPositionTranslation = projectVectorOntoLine(
        draggingVector,
        math.subtract(startPoint, prevPoint)
      );
      endPositionTranslation = projectVectorOntoLine(
        draggingVector,
        math.subtract(nextPoint, endPoint)
      );
    } else {
      startPositionTranslation = draggingVector;
      endPositionTranslation = draggingVector;
    }

    // Calculate current edge midpoint for feedback
    const currentMidpoint = math.multiply(
      0.5,
      math.add(
        math.add(startPoint, startPositionTranslation),
        math.add(endPoint, endPositionTranslation)
      )
    ) as Point;

    // Update drag feedback state
    if (edgeDragOriginRef.current) {
      const direction = math.subtract(
        currentMidpoint,
        edgeDragOriginRef.current
      ) as Point;
      const length = math.norm(direction) as number;
      const normalizedDirection =
        length > 0
          ? (math.divide(direction, length) as Point)
          : ([0, 0, 0] as Point);

      setDragFeedback({
        type: 'edge',
        originPoint: edgeDragOriginRef.current,
        currentPoint: currentMidpoint,
        ringId,
        pointId: startPointId,
        direction: normalizedDirection,
      });
    }

    dispatch({
      type: PolygonDrawingActionType.MOVE_COMPLETE_RING_EDGE,
      startPositionTranslation: startPositionTranslation,
      endPositionTranslation: endPositionTranslation,
      startPointId,
      ringId,
    });

    if (last) {
      setIsDragging(false);
      // Keep the drag feedback visible after drag ends
    }
  };

  function projectVectorOntoLine(vector: Point, line: Point) {
    const normLine = math.norm(line);
    if (normLine === 0) return [0, 0, 0] as Point;
    const lineUnitVector = math.divide(line, normLine) as Point;
    const projectionLength = math.dot(vector, lineUnitVector);
    return math.multiply(
      math.round(lineUnitVector, 1),
      projectionLength
    ) as Point;
  }

  const handleEdgeDoubleClick = (
    event: React.MouseEvent,
    edgeId: string,
    ringId: string
  ) => {
    if (event.button !== 0) {
      return;
    }

    const edgeIndex = edgesForCompletedRings[ringId].findIndex(
      (edge) => edge.id === edgeId
    );
    const startPointId = state.completedRings.find((ring) => ring.id === ringId)
      ?.linearRing[edgeIndex].id;
    invariant(startPointId, 'start point not found');
    const newPoint = getPositionOnEdge(
      event,
      edgesForCompletedRings[ringId][edgeIndex].segment,
      viewer
    );
    dispatch({
      type: PolygonDrawingActionType.SPLIT_COMPLETE_RING_EDGE,
      splitPosition: newPoint,
      startPointId,
      ringId,
    });
  };

  const handleRemoveRing = (ringId: string) => {
    dispatch({
      type: PolygonDrawingActionType.REMOVE_COMPLETE_RING,
      ringId,
    });
  };

  // Handler for when user submits a length after dragging
  const handleDragLengthSubmit = useCallback(
    (lengthInMeters: number) => {
      if (!dragFeedback || lengthInMeters <= 0) {
        return;
      }

      // Convert the submitted length from meters to model units
      const lengthInModelUnits = lengthInMeters / unitScale;

      // Calculate new position based on direction and submitted length
      const newPosition = math.add(
        dragFeedback.originPoint,
        math.multiply(dragFeedback.direction, lengthInModelUnits)
      ) as Point;

      onBeforeGeometryChange();

      if (dragFeedback.type === 'vertex') {
        dispatch({
          type: PolygonDrawingActionType.MOVE_COMPLETE_RING_POINT,
          position: newPosition,
          pointId: dragFeedback.pointId,
          ringId: dragFeedback.ringId,
        });
      } else {
        // For edges, calculate the translation vector
        const translation = math.subtract(
          newPosition,
          dragFeedback.currentPoint
        ) as Point;

        dispatch({
          type: PolygonDrawingActionType.MOVE_COMPLETE_RING_EDGE,
          startPositionTranslation: translation,
          endPositionTranslation: translation,
          startPointId: dragFeedback.pointId,
          ringId: dragFeedback.ringId,
        });
      }

      // Keep the feedback visible with updated position for subsequent edits
      setDragFeedback({
        ...dragFeedback,
        currentPoint: newPosition,
      });
    },
    [dragFeedback, unitScale, onBeforeGeometryChange, dispatch]
  );

  return (
    <>
      {isOneClickShapeMode && completedMultipolygon && state.plane ? (
        <MultipolygonHoles
          originalRings={state.completedRings}
          multipolygon={completedMultipolygon}
          plane={state.plane}
          viewer={viewer}
          onHoleClick={(ringId) => handleRemoveRing(ringId)}
        />
      ) : null}
      {completedMultipolygon &&
        state.plane &&
        (thickness ? (
          <ExtrudedPolygon
            multipolygon={completedMultipolygon}
            plane={state.plane}
            viewer={viewer}
            isClosed={true}
            thickness={thickness}
          />
        ) : (
          <Polygon
            multipolygon={completedMultipolygon}
            plane={state.plane}
            viewer={viewer}
            isClosed={true}
          />
        ))}
      {Object.entries(edgesForCompletedRings).map(([ringId, edgesForRing]) =>
        edgesForRing.map(({ id: edgeId, segment }) => {
          const color = getEdgeColor(
            true,
            !invalidEdgesForCompletedRings.includes(edgeId)
          );
          return (
            <DraggableEdge
              key={edgeId}
              onDoubleClick={(event) =>
                handleEdgeDoubleClick(event, edgeId, ringId)
              }
              onEdgeDrag={(gestureState) =>
                handleEdgeDrag(gestureState, edgeId, ringId)
              }
              segment={segment}
              backgroundColor={color}
              labelColorScheme={getColorSchemeFromColor(color)}
              viewer={viewer}
            />
          );
        })
      )}

      {state.completedRings.filter(Boolean).map((ring) => {
        return ring.linearRing.map(({ point, id: pointId }, index) => {
          if (index === ring.linearRing.length - 1) {
            return null;
          }
          const isSelected = selectedVertices.some(
            (vertex) =>
              vertex.type === 'completed' &&
              vertex.ringId === ring.id &&
              vertex.pointId === pointId
          );
          return (
            <DraggableVertex
              key={pointId}
              isSelected={isSelected}
              point={point}
              viewer={viewer}
              onDoubleClick={() => {
                dispatch({
                  type: PolygonDrawingActionType.REMOVE_COMPLETE_RING_POINT,
                  ringId: ring.id,
                  pointId,
                });
              }}
              onClick={(event) =>
                onVertexSelectionClick(ring.id, pointId, event.shiftKey)
              }
              onVertexDrag={(gestureState) =>
                handleVertexDrag(gestureState, pointId, ring.id)
              }
            />
          );
        });
      })}

      {/* Render drag feedback line when dragging or after drag completes */}
      {dragFeedback && (
        <DragFeedbackLine
          origin={dragFeedback.originPoint}
          current={dragFeedback.currentPoint}
          viewer={viewer}
          isEditable={!isDragging}
          isDragging={isDragging}
          onLengthSubmit={handleDragLengthSubmit}
        />
      )}
    </>
  );
}

const isEqualRings = (ring1: LinearRing, ring2: LinearRing) => {
  return (
    ring1.length === ring2.length &&
    ring1.every(
      (point, index) => (math.distance(ring2[index], point) as number) < 0.0001
    )
  );
};

// A component for filling the holes of a multipolygon in one click shape mode
const MultipolygonHoles = ({
  originalRings,
  multipolygon,
  viewer,
  onHoleClick,
}: {
  originalRings: LinearRing3WithId[];
  multipolygon: MultiPolygon;
  plane: Plane;
  viewer: Autodesk.Viewing.GuiViewer3D;
  onHoleClick: (ringId: string) => void;
}) => {
  // Just use an empty object so we can notify the component when the camera changes
  const [cameraPosition, setCameraPosition] = useState({});

  useCameraChangedEvent(
    viewer,
    useCallback(() => {
      setCameraPosition({});
    }, [])
  );

  const holes: MultiPolygon = useMemo(() => {
    return {
      polygons: multipolygon.polygons.flatMap((polygon) =>
        polygon.interiors.map((interior) => ({
          exterior: interior,
          interiors: [],
        }))
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multipolygon, cameraPosition]);

  const ringsWithPathData = useMemo(() => {
    return holes.polygons.map((polygon) => ({
      ring: polygon.exterior,
      pathData: getPolygon2PathData(
        transformPolygonCoordinates(polygon, (p) => projectInDOM(p, viewer))
      ),
    }));
  }, [holes.polygons, viewer]);

  // Find back to the original ring matching the clicked ring
  const handleHoleClick = (ring: LinearRing) => {
    const ringId = originalRings.find((originalRing) =>
      isEqualRings(ring, getPositions(originalRing.linearRing))
    )?.id;
    if (ringId) {
      onHoleClick(ringId);
    } else {
      console.warn('Could not find ring id for clicked hole');
    }
  };

  return (
    <chakra.svg
      w="100%"
      h="100%"
      position={'absolute'}
      zIndex={1}
      pointerEvents={'none'}
    >
      <chakra.g
        pointerEvents={'auto'}
        opacity={0.8}
        fill="gray.500"
        cursor="pointer"
      >
        {ringsWithPathData.map(({ ring, pathData }, index) => (
          <chakra.path
            key={index}
            d={pathData}
            _hover={{
              filter: 'brightness(150%)',
            }}
            onClick={() => handleHoleClick(ring)}
          />
        ))}
      </chakra.g>
    </chakra.svg>
  );
};

const getEdgeColor = (isClosed?: boolean, isValid?: boolean) => {
  if (!isValid) {
    return 'red.400';
  } else if (isClosed) {
    return 'teal.500';
  } else {
    return 'yellow.300';
  }
};

/**
 * Maps a Chakra color like "teal.500" to a colorScheme like "teal"
 */
const getColorSchemeFromColor = (color: string): string => {
  return color.split('.')[0];
};
